<template>
  <page-view
    class="subpage collection"
    :subpage="!!collection"
    :title="collection ? collection.metadata.title : id === 'create' ? 'Create new collection' : ''"
    :id="collection ? collection.id : ''"
    :longTitle="!!collection"
    v-if="state === ''"
    backLink="/collections"
    backText="Back to Collections"
    :key="$route.hash"
    ref="pageview"
  >
    <template v-slot:id>
      <id :id="collection.id" v-if="collection && collection.id" />
    </template>
    <template v-slot:header-right>
      <template v-if="!collection">
        <Button :class="`${canCreate ? '' : 'disable'}`" :loading="creating" @click="createCollection()">Create</Button>
      </template>
      <template v-else-if="collection">
        <dots class="border left m-r-2">
          <ul>
            <li @click="deleteCollection">
              <i class="icon-close-medium" />
                Delete collection
            </li>
            <a :href="`https://${fqdn}.streamify.io/play/collections/${id}`" v-if="featuresEnabled.includes('ppv')" target="_blank">
              <i class="icon-eye" />
              View on Play
            </a>
          </ul>
        </dots>
      </template>
    </template>
    <card-minimal title="Description">
      <div class="d-f fd-c fd-md-r p-t-3 p-b-2 p-x-1">
        <div class="p-x-3 f-1">
          <p class="card-content-title">
            <strong>Title</strong>
          </p>
          <form-input type="text" ref="title" v-model="data.title"  />
          <p class="card-content-title m-t-2">
            <strong>Description</strong>
            <span v-if="data.liveshopping">Appears on poster</span>
          </p>
          <form-textarea class="small m-b-2" :toolbar="['bold', 'ul', 'link']" height="185" v-model="data.description" />
          <template v-if="featuresEnabled.includes('ppv')">
            <p class="card-content-title m-t-2">
              <strong>Tags</strong>
              <span>Automatically links broadcast and videos with the same tags to collection</span>
            </p>
            <FormTags class="f-w m-b-1" placeholder="Add tags" :existingTags="data.tags" @data="setTags" v-if="data.tags !== null"/>
            <template v-if="collectionItems.length > 0">
              <div class="d-f f-wrap ai-c m-b-2" style="line-height: 18px;">
                <p style="color: gray; font-size: 14px; margin-right: 5px;">Item{{ collectionItems.length > 1 ? 's' : ''}}:</p>
                <!-- eslint-disable-next-line -->
                <router-link v-for="(item, index) in collectionItems" :key="item.id" :to="item.type === 'live' ? `/broadcast/${item.data.id}` : `/video/${item.id}`" class="link blue m-r-1">{{ item.data.metadata.title }}{{ index < collectionItems.length - 1 ? ',' : ''}}</router-link>
              </div>
            </template>
          </template>
        </div>
        <div class="p-x-3 f-1">
          <p class="card-content-title">
            <strong>Poster image</strong>
          </p>
          <media-upload class="m-b-2" :legacy="true" @images="setImage" :unsplash="true" :multiple="false" title="Poster image" :overwrite="true" size="h/768" :data="{ force: true, images: data.currentPoster ? [{ value: data.currentPoster }] : [] }" />
        </div>
      </div>
      <div class="p-x-4 p-b-4" v-if="collection">
        <Button :class="`large ${canUpdate || !updating ? '' : 'disable'}`" :loading="updating" @click="updateCollection">Update</Button>
      </div>
    </card-minimal>
    <card-minimal title="Features" class="m-t-4" v-if="[1, 6, 15, 16].includes(user.permission_id) && collection">
      <div class="features p-a-3">
        <toggle class="group" :value="data.published" @change="togglePublished" text="Show on Play" explanation="Make content visible in public lists or keep it private"></toggle>
        <toggle class="group" :value="data.featured" @change="toggleFeatured" text="Featured" explanation="Feature this collection on the homepage of your Play"></toggle>
      </div>
    </card-minimal>
  </page-view>
</template>

<script>

import Dots from '@/components/Dots.vue';
import MediaUpload from '@/components/MediaUpload.vue';
import Toggle from '@/components/toggle.vue';

export default {
  components: {
    Dots,
    MediaUpload,
    Toggle,
  },
  props: {
    id: {
      type: String,
      default: null,
    },
  },
  computed: {
    collection() {
      return this.$store.getters['Collections/current'];
    },
    collectionItems() {
      return this.$store.getters['Collections/collectionItems'];
    },
    /**
     * Check if all fields are valid to update a broadcast
     * @returns {Boolean}
     */
    canUpdate() {
      const {
        data: {
          title,
        },
      } = this;

      if (title === '') {
        return false;
      }

      return true;
    },
    /**
     * Check if all fields are valid to update a broadcast
     * @returns {Boolean}
     */
    canCreate() {
      const {
        data: {
          title,
        },
      } = this;

      if (title === '') {
        return false;
      }

      return true;
    },
  },
  methods: {
    /**
     * Set a new poster image for the collection
     * @param {FormData} image
     * @returns {Void}
     */
    setImage(image) {
      this.data.newPoster = image;
    },
    /**
     * Set the tags for the collections
     * @param {Array} tags
     * @returns {Void}
     */
    setTags(tags) {
      this.data.tags = tags;

      this.fetchCollectionItems(tags);
    },
    /**
     * Fetch collection items based on tags
     * @returns {Void}
     */
    async fetchCollectionItems() {
      await this.$store.dispatch('Collections/getCollectionItems', this.data.tags);
    },
    /**
     * Toggle published state of collection
     * @param {Boolean} bool
     */
    async togglePublished(bool) {
      this.data.published = bool;
      await this.$store.dispatch('Collections/update', {
        id: this.collection.id,
        play: {
          published: this.data.published,
        },
      });
    },
    /**
     * Toggle featured state of collection
     * @param {Boolean} bool
     */
    async toggleFeatured(bool) {
      this.data.featured = bool;
      await this.$store.dispatch('Collections/update', {
        id: this.collection.id,
        play: {
          featured: this.data.featured,
        },
      });
    },
    /**
     * Update the collection
     * @returns {Void}
     */
    async updateCollection() {
      const {
        title,
        description,
        tags,
        currentPoster,
        newPoster,
      } = this.data;

      this.updating = true;

      await this.$store.dispatch('Collections/update', {
        id: this.collection.id,
        metadata: {
          title,
          description,
        },
        tags,
      });

      if (currentPoster !== newPoster) {
        await this.$store.dispatch('Collections/poster', { id: this.collection.id, file: newPoster });
      }

      await this.fetchCollectionItems();

      setTimeout(() => {
        this.updating = false;
      }, 1000);
    },
    /**
     * Create the collections
     * @returns {Void}
     */
    async createCollection() {
      this.creating = true;

      const {
        title,
        description,
        tags,
        newPoster,
      } = this.data;

      const collectionId = await this.$store.dispatch('Collections/create', {
        metadata: {
          title,
          description,
        },
        tags,
      });
      if (!collectionId) {
        swal({ title: 'Error', text: 'Something went wrong, please try again', type: 'error' });
        this.creating = false;
        return;
      }

      if (newPoster) {
        await this.$store.dispatch('Collections/poster', { id: collectionId, file: newPoster });
      }

      await this.$store.dispatch('Collections/getCollection', collectionId);

      this.setCollectionData();

      this.$router.push(`/collection/${collectionId}`);

      this.creating = false;
    },
    /**
     * Deletes the collection
     * @returns {Void}
     */
    async deleteCollection() {
      const { collection: { id } } = this;

      const willDelete = await swal({
        title: this.$t('common.are_you_sure'),
        text: this.$t('collections.no_recovery'),
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      });
      if (!willDelete) return;

      const deleted = await this.$store.dispatch('Collections/delete', id);
      if (!deleted) {
        swal({ title: this.$t('errors.error_occured'), text: this.$t('collections.could_not_delete'), icon: 'error' });
        return;
      }

      this.$router.push({ path: '/collections' });
    },
    /**
     * Set the collectiondata
     * @returns {Void}
     */
    setCollectionData() {
      const { collection } = this;

      const [poster] = collection.resources.filter(item => (typeof item.name !== 'undefined' && item.name.indexOf('640x360') >= 0 ? item : null));

      this.data = {
        title: collection.metadata.title,
        description: collection.metadata.description,
        published: collection.play.published,
        featured: collection.play.featured,
        currentPoster: poster ? `https://${this.fqdn}.streamify.io/resources/${poster.name}` : null,
        newPoster: poster ? `https://${this.fqdn}.streamify.io/resources/${poster.name}` : null,
        tags: collection.tags,
      };

      this.fetchCollectionItems();
    },
  },
  data() {
    return {
      data: {
        title: '',
        description: '',
        published: false,
        featured: false,
        currentPoster: null,
        newPoster: null,
        tags: [],
      },
      updating: false,
      creating: false,
      state: 'loading',
    };
  },
  async mounted() {
    if (this.id !== 'create') {
      await this.$store.dispatch('Collections/getCollection', this.id);
      this.setCollectionData();
    }

    this.$store.dispatch('Tags/getTags', true);

    this.state = '';
  },
  beforeDestroy() {
    this.$store.dispatch('Collections/unset');
  },
};
</script>

<style lang="scss">
.collection {
  .media-upload {
    .card-minimal {
      .card-title {
        display: none;
      }
      .card-content {
        border-radius: 0;
        box-shadow: unset;

        & > .p-a-3 {
          padding: 0;
        }
      }
    }
    .image-gallery {
      .images {
        background-position: 50% 50%;
        background-size: cover;
      }
    }
    .upload-area:after, .image-gallery .images {
      padding-bottom: 56.6%;
      padding-top: 0;
    }
  }

  .features {
    display: flex;
    flex-wrap: wrap;

    & > * {
      width: calc(100% - 12px);
      margin: 6px;
    }

    @media (min-width: 768px) {
      & > * {
        width: calc(50% - 12px);
      }
    }
  }
}
</style>
